import React, { useState, useEffect } from 'react';
import '../styles/WellForm.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../api/AuthContext';
import { Typography, Alert, Divider, Paper, Box } from '@mui/material';


// Get App Url
const appUrl = process.env.REACT_APP_APP_URL;

if (!appUrl) {
    throw new Error("REACT_APP_APP_URL is not defined. Please set it in your .env file.");
}

// Section title component for better organization
const SectionTitle = ({ title }) => (
  <div className="section-title">
    <Divider textAlign="left" sx={{ my: 2 }}>
      <Typography variant="h6" component="span" sx={{ px: 2, fontWeight: 'bold' }}>
        {title}
      </Typography>
    </Divider>
  </div>
);

const WellForm = ({ well, disabled, onWellUpdated }) => {
  const [customerName, setCustomerName] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [jobNumber, setJobNumber] = useState('');
  const [rigNo, setRigNo] = useState('');
  const [area, setArea] = useState('');
  const [customerRepresentative, setCustomerRepresentative] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [api, setAPI] = useState('xx-xxx-xxxxx'); // Default value for the API field
  const [dwrDate, setDWRDate] = useState('');
  const [status, setStatus] = useState('');
  const [wellName, setWellName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [rigs, setRigs] = useState([]);
  const [group, setGroup] = useState('');
  const [state, setState] = useState('');
  const [county, setCounty] = useState('');
  const [afePo, setAfePo] = useState('');
  const [bidPrice, setBidPrice] = useState('');
  const [estimatedDays, setEstimatedDays] = useState('');
  const [totalCharges, setTotalCharges] = useState('');
  const [netsuiteId, setNetsuiteId] = useState('');
  const [selectedCustomerNetsuiteId, setSelectedCustomerNetsuiteId] = useState('');
  const [selectedRigNetsuiteId, setSelectedRigNetsuiteId] = useState('');
  const [workingHours, setWorkingHours] = useState('10'); // Default to 10 hours
  const [allocatedTurnkeyHours, setAllocatedTurnkeyHours] = useState('');
  const [requestedTurnkeyHours, setRequestedTurnkeyHours] = useState('');
  const [remainingTurnkeyHours, setRemainingTurnkeyHours] = useState('');
  const [turnkeyCharges, setTurnkeyCharges] = useState('');
  const [estimatedRevenue, setEstimatedRevenue] = useState('');
  const [bidDaysDelta, setBidDaysDelta] = useState('');
  const [wellheadCutoffComplete, setWellheadCutoffComplete] = useState(false);
  const [notes, setNotes] = useState('');
  const [movingBackOn, setMovingBackOn] = useState(false);
  const [isLeaking, setIsLeaking] = useState(false);
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [originalValues, setOriginalValues] = useState({});

  useEffect(() => {
    const fetchCustomersAndRigs = async () => {
      try {
        const token = localStorage.getItem('my-jwt');
        const [customersResponse, rigsResponse] = await Promise.all([
          axios.get(`${appUrl}/jmrapp/customers/`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }),
          axios.get(`${appUrl}/jmrapp/rigs/`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }),
        ]);

        setCustomers(customersResponse.data);
        setRigs(rigsResponse.data);
      } catch (error) {
        console.error('Error fetching customers and rigs:', error);
      }
    };

    fetchCustomersAndRigs();
  }, []);

  useEffect(() => {
    if (well && customers.length > 0 && rigs.length > 0) {
      // Ensure well and nested properties are defined before accessing them
      setCustomerName(well.customer?.customer_name || '');
      setCustomerId(well.customer?.customer_id?.toString() || '');
      setCustomerAddress(well.customer_address || '');
      setCounty(well.county || '');
      setState(well.state || '');
      setJobNumber(well.rrc_job_number || '');
      setArea(well.area || '');
      setCustomerRepresentative(well.customer_representative || '');
      setLatitude(well.latitude || '');
      setLongitude(well.longitude || '');
      setAPI(well.api || 'xx-xxx-xxxxx');
      setDWRDate(well.dwrDate || '');
      setStatus(well.status || '');
      setWellName(well.name || '');
      setAfePo(well.afe_po || '');
      setNetsuiteId(well.netsuite_id || '');
      setTotalCharges(well?.total_charges || '');
      setBidPrice(well?.bid_price || '');
      setEstimatedDays(well?.estimated_days || '');
      setWorkingHours(well?.working_hours?.toString() || '10');
      setAllocatedTurnkeyHours(well?.total_turnkey_hours?.toString() || '0');
      setRequestedTurnkeyHours(well?.total_requested_turnkey_hours?.toString() || '0');
      setRemainingTurnkeyHours(well?.total_hours_remaining?.toString() || '0');
      setWellheadCutoffComplete(well?.wellhead_cutoff_complete || false);
      setNotes(well?.notes || '');
      setMovingBackOn(well?.moving_back_on || false);
      setIsLeaking(well?.is_leaking || false);
      
      // Calculate turnkey charges based on hourly rate and allocated hours
      const hourlyRate = well?.hourly_turnkey_price || 0;
      const allocatedHours = well?.total_turnkey_hours || 0;
      setTurnkeyCharges((hourlyRate * allocatedHours).toString());
      
      // Set estimated revenue
      setEstimatedRevenue(well?.estimated_revenue?.toString() || '0');
      
      // Set the bid days delta value
      // This will need to be calculated on the backend and returned in the API response
      setBidDaysDelta(well?.bid_days_delta?.toString() || 'N/A');
      
      const selectedCustomer = customers.find(c => c.id === well.customer?.id); // Ensure 'well.customer' is defined

      if (selectedCustomer) {
        setCustomerId(selectedCustomer.id.toString()); // This might be redundant
        setSelectedCustomerNetsuiteId(selectedCustomer.ns_iid || well.customer_nsid || '');
      }

      const selectedRig = rigs.find(r => r.name === well.assigned_rig?.name); // Ensure 'well.assigned_rig' is defined
      if (selectedRig) {
        setRigNo(selectedRig.id.toString());
        setSelectedRigNetsuiteId(selectedRig.netsuite_id || '');
      }
    }
  }, [well, customers, rigs]);

  const formatAPI = (value) => {
    // Remove any non-digit characters
    const digits = value.replace(/\D/g, '');

    // Format to "xx-xxx-xxxxx" with placeholders
    let formatted = "xx-xxx-xxxxx";
    
    // Replace placeholder characters with user input
    for (let i = 0; i < digits.length && i < 10; i++) {
      formatted = formatted.replace('x', digits[i]);
    }
    
    return formatted;
  };

  const handleAPIChange = (e) => {
    const formattedAPI = formatAPI(e.target.value);
    setAPI(formattedAPI);
  };

  const updateNetSuite = async (data, netsuiteId) => {
    try {
      const token = localStorage.getItem('my-jwt');
      const method = 'PATCH';
      const url = `${appUrl}/proxyapp/proxy/`;
      const netsuiteResponse = await axios({
        method,
        url,
        data: { ...data, netsuite_id: netsuiteId },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      return netsuiteResponse.status === 200;
    } catch (error) {
      console.error('Error updating NetSuite:', error);
      return false;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const netsuiteData = {
      companyName: wellName,
      projectExpenseType: -2,
      custentity_api_number: api,
      custentity_rig: selectedRigNetsuiteId,
      entityStatus: 2,
      parent_netsuite_id: selectedCustomerNetsuiteId,
      subsidiary: { id: "4" },
      custentitycustentity_latitude: latitude || null,
      custentitycustentity_longitude: longitude || null,
      custentitybid_price: bidPrice || null,
      custentityestimated_days: estimatedDays || null,
    };

    if (api.includes('x')) {
      alert('The API number is incomplete. Please ensure the API number is fully entered without any "x"s.');
      setIsEditing(false);
      return;
    }

    const success = await updateNetSuite(netsuiteData, netsuiteId);

    if (success) {
      try {
        const token = localStorage.getItem('my-jwt');
        const response = await axios.patch(`${appUrl}/jmrapp/wells/${well.id}/`, {
          api,
          area,
          customer_representative: customerRepresentative,
          latitude,
          county,
          state,
          longitude,
          rrc_job_number: jobNumber,
          status,
          name: wellName,
          afe_po: afePo,
          customer_id: customerId,
          assigned_rig: parseInt(rigNo),
          assigned_rig_id: parseInt(rigNo),
          bid_price: bidPrice || null,
          estimated_days: estimatedDays || null,
          working_hours: parseInt(workingHours) || 10,
          wellhead_cutoff_complete: wellheadCutoffComplete,
          notes: notes,
          moving_back_on: movingBackOn,
          is_leaking: isLeaking,
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        setIsEditing(false);
        if (onWellUpdated) {
          onWellUpdated(response.data);
        }
        navigate('/', { state: { updatedWell: response.data } });
      } catch (error) {
        console.error('Error updating well:', error);
      }
    }
  };

  const handleEditClick = (event) => {
    event.preventDefault();
    if (!disabled) {
      // Store current values before entering edit mode
      setOriginalValues({
        customerId,
        rigNo,
        area,
        wellName,
        api,
        status,
        afePo,
        jobNumber,
        customerRepresentative,
        county,
        state,
        latitude,
        longitude,
        bidPrice,
        estimatedDays,
        workingHours
      });
      
      setIsEditing((prevState) => !prevState);
    }
  };

  const handleCancel = () => {
    // Restore original values
    setCustomerId(originalValues.customerId);
    setRigNo(originalValues.rigNo);
    setArea(originalValues.area);
    setWellName(originalValues.wellName);
    setAPI(originalValues.api);
    setStatus(originalValues.status);
    setAfePo(originalValues.afePo);
    setJobNumber(originalValues.jobNumber);
    setCustomerRepresentative(originalValues.customerRepresentative);
    setCounty(originalValues.county);
    setState(originalValues.state);
    setLatitude(originalValues.latitude);
    setLongitude(originalValues.longitude);
    setBidPrice(originalValues.bidPrice);
    setEstimatedDays(originalValues.estimatedDays);
    setWorkingHours(originalValues.workingHours);
    
    // Exit edit mode
    setIsEditing(false);
  };

  // Add a formatting function at the top of the component
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '';
    return `$${Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Add this function after formatCurrency
  const getChargesColor = (totalCharges, bidPrice) => {
    if (!bidPrice || bidPrice <= 0) return ''; // No color if there's no valid bid price
    
    const percentage = (totalCharges / bidPrice) * 100;
    
    if (percentage <= 75) return '#e8f5e9';  // Light green background
    if (percentage <= 90) return '#fff3e0';  // Light orange background
    return '#ffebee';  // Light red background
  };

  // Add a new helper function to format the bid days delta
  const formatBidDaysDelta = (delta) => {
    if (delta === null || delta === undefined || delta === 'N/A') {
      return 'N/A';
    }
    
    const numDelta = Number(delta);
    return numDelta >= 0 ? `+${numDelta}` : `${numDelta}`;
  };
  
  // Add a helper to get the color for the bid days delta
  const getBidDaysDeltaColor = (delta) => {
    if (delta === null || delta === undefined || delta === 'N/A') {
      return '';
    }
    
    const numDelta = Number(delta);
    if (numDelta <= 0) return '#e8f5e9'; // Green for on/under budget (negative or zero)
    if (numDelta <= 2) return '#fff3e0'; // Orange for slightly over (1-2 days)
    return '#ffebee'; // Red for significantly over (3+ days)
  };

  const canEdit = (authState.user?.is_manager || authState.user?.is_superuser) && !disabled;

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <form className='form' onSubmit={handleSubmit}>
        {/* Basic Well Information Section */}
        <SectionTitle title="Basic Well Information" />
        <div className='formRow'>
          <div className='formCol'>
            <label className='formLabel' htmlFor="customer">Customer <span className="required">*</span></label>
            <select
              className='formInput'
              id="customer"
              value={customerId}
              onChange={(e) => {
                setCustomerId(e.target.value);
                const selectedCustomer = customers.find(customer => customer.id.toString() === e.target.value);
                setCustomerName(selectedCustomer?.name || '');
                setCustomerAddress(selectedCustomer?.address || '');
                setSelectedCustomerNetsuiteId(selectedCustomer?.ns_iid || '');
              }}
              disabled={!isEditing}
              required
            >
              <option value="">Select a customer</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id.toString()}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>

          <div className='formCol'>
            <label className='formLabel' htmlFor="assignedRig">Assigned Rig <span className="required">*</span></label>
            <select
              className='formInput'
              id="assignedRig"
              value={rigNo}
              onChange={(e) => {
                setRigNo(e.target.value);
                const selectedRig = rigs.find(rig => rig.id.toString() === e.target.value);
                setSelectedRigNetsuiteId(selectedRig?.netsuite_id || '');
              }}
              disabled={!isEditing}
              required
            >
              <option value="">Select a rig</option>
              {rigs.map((rig) => (
                <option key={rig.id} value={rig.id.toString()}>
                  {rig.name}
                </option>
              ))}
            </select>
          </div>

          <div className='formCol'>
            <label className='formLabel' htmlFor="area">Area Category <span className="required">*</span></label>
            <select 
              className='formInput' 
              id="area" 
              value={area} 
              onChange={(e) => setArea(e.target.value)} 
              disabled={!isEditing} 
              required
            >
              <option value="">Select an area</option>
              <option value="Permian">Permian</option>
              <option value="DJ Basin">DJ Basin</option>
              <option value="San Juan Basin">San Juan Basin</option>
              <option value="Deleware Basin">Delaware Basin</option>
              <option value="Reclamation">Reclamation</option>
            </select>
          </div>
        </div>

        <div className='formRow'>
          <div className='formCol'>
            <label className='formLabel' htmlFor="wellName">Well Name <span className="required">*</span></label>
            <input 
              className='formInput' 
              type="text" 
              id="wellName"
              value={wellName} 
              onChange={(e) => setWellName(e.target.value)} 
              disabled={!isEditing} 
              required 
            />
          </div>
          
          <div className='formCol'>
            <label className='formLabel' htmlFor="api">API Number <span className="required">*</span></label>
            <input 
              className='formInput' 
              type="text" 
              id="api"
              value={api} 
              onChange={handleAPIChange} 
              disabled={!isEditing} 
              required 
            />
          </div>

          <div className='formCol'>
            <label className='formLabel' htmlFor="wellStatus">Well Status <span className="required">*</span></label>
            <select 
              className='formInput' 
              id="wellStatus" 
              value={status} 
              onChange={(e) => setStatus(e.target.value)} 
              disabled={!isEditing} 
              required
            >
              <option value="In Progress">In Progress</option>
              <option value="Awarded">Awarded</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
        </div>

        {/* Additional Information Section */}
        <SectionTitle title="Additional Information" />
        <div className='formRow'>
          <div className='formCol'>
            <label className='formLabel' htmlFor="afePo">AFE/PO No.</label>
            <input 
              className='formInput' 
              type="text" 
              id="afePo"
              value={afePo} 
              onChange={(e) => setAfePo(e.target.value)} 
              disabled={!isEditing} 
            />
          </div>

          <div className='formCol'>
            <label className='formLabel' htmlFor="jobNumber">Job Number</label>
            <input 
              className='formInput' 
              type="text" 
              id="jobNumber"
              value={jobNumber} 
              onChange={(e) => setJobNumber(e.target.value)} 
              disabled={!isEditing} 
            />
          </div>

          <div className='formCol'>
            <label className='formLabel' htmlFor="customerRepresentative">Customer Representative</label>
            <input 
              className='formInput' 
              type="text" 
              id="customerRepresentative"
              value={customerRepresentative} 
              onChange={(e) => setCustomerRepresentative(e.target.value)} 
              disabled={!isEditing} 
            />
          </div>
        </div>

        <div className='formRow'>
          <div className='formCol'>
            <label className='formLabel' htmlFor="county">County</label>
            <input 
              className='formInput' 
              type="text" 
              id="county"
              value={county} 
              onChange={(e) => setCounty(e.target.value)} 
              disabled={!isEditing} 
            />
          </div>

          <div className='formCol'>
            <label className='formLabel' htmlFor="state">State</label>
            <input 
              className='formInput' 
              type="text" 
              id="state"
              value={state} 
              onChange={(e) => setState(e.target.value)} 
              disabled={!isEditing} 
            />
          </div>
        </div>

        <div className='formRow'>
          <div className='formCol'>
            <label className='formLabel' htmlFor="latitude">Latitude</label>
            <input 
              className='formInput' 
              type="number" 
              id="latitude"
              value={latitude} 
              onChange={(e) => setLatitude(e.target.value.toString())} 
              disabled={!isEditing} 
            />
            <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
              <Typography variant="caption">
                Latitude value must not exceed 6 decimal places
              </Typography>
            </Alert>
          </div>

          <div className='formCol'>
            <label className='formLabel' htmlFor="longitude">Longitude</label>
            <input 
              className='formInput' 
              type="number" 
              id="longitude"
              value={longitude} 
              onChange={(e) => setLongitude(e.target.value)} 
              disabled={!isEditing} 
            />
            <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
              <Typography variant="caption">
                Longitude value must not exceed 6 decimal places
              </Typography>
            </Alert>
          </div>
        </div>

        {/* Financial Information Section */}
        <SectionTitle title="Bid Information" />
        <div className='formRow'>
          {(authState.user?.is_superuser || authState.user?.is_district_manager || authState.user?.is_manager) && (
            <div className='formCol'>
              <label className='formLabel' htmlFor="bidPrice">Bid Price</label>
              <input 
                className='formInput'
                type="number"
                id="bidPrice"
                step="0.01"
                value={bidPrice}
                onChange={(e) => setBidPrice(e.target.value)}
                disabled={!authState.user?.is_superuser || !isEditing}
                placeholder="0.00"
              />
              <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                <Typography variant="caption">
                  Enter bid price without currency symbol or commas - Required for Turnkey Charges Calculation
                </Typography>
              </Alert>
            </div>
          )}

          {(authState.user?.is_superuser || authState.user?.is_district_manager || authState.user?.is_manager) && (
            <div className='formCol'>
              <label className='formLabel' htmlFor="estimatedDays">Estimated Days</label>
              <input 
                className='formInput'
                type="number"
                id="estimatedDays"
                step="1"
                value={estimatedDays}
                onChange={(e) => setEstimatedDays(e.target.value)}
                disabled={!authState.user?.is_superuser || !isEditing}
                placeholder="0"
              />
              <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                <Typography variant="caption">
                  Required for Turnkey Charges Calculation
                </Typography>
              </Alert>
            </div>
          )}

          <div className='formCol'>
            <label className='formLabel' htmlFor="workingHours">Avg Working Hours</label>
            <input 
              className='formInput'
              type="number"
              id="workingHours"
              step="1"
              min="1"
              max="24"
              value={workingHours}
              onChange={(e) => setWorkingHours(e.target.value)}
              disabled={!isEditing}
              placeholder="10"
            />
            <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
              <Typography variant="caption">
                Required for Turnkey Charges Calculation
              </Typography>
            </Alert>
          </div>
        </div>

        {/* Total Charges Information */}
        <div className='formRow'>
          <div className='formCol'>
            <label className='formLabel'>Bid Days' Delta</label>
            <input 
              className='formInput'
              type="text"
              value={formatBidDaysDelta(bidDaysDelta)}
              disabled={true}
              title="Difference between estimated days and actual number of unique dates across DWRs"
              style={{
                backgroundColor: getBidDaysDeltaColor(bidDaysDelta),
                transition: 'background-color 0.3s',
                fontWeight: 'bold'
              }}
            />
            <Alert severity="info" sx={{ mt: 1, py: 0 }}>
              <Typography variant="caption">
                Positive value means project is over the estimated days
              </Typography>
            </Alert>
          </div>
          
          <div className='formCol'>
            <label className='formLabel'>Estimated Revenue</label>
            <input 
              className='formInput'
              type="text"
              value={formatCurrency(estimatedRevenue)}
              disabled={true}
            />
          </div>
        </div>

        {/* Turnkey Hours Information Section */}
        <SectionTitle title="Turnkey Hours" />
        <div className='formRow'>
          <div className='formCol'>
            <label className='formLabel'>Allocated Turnkey Hours</label>
            <input 
              className='formInput'
              type="text"
              value={allocatedTurnkeyHours}
              disabled={true}
            />
          </div>
          
          <div className='formCol'>
            <label className='formLabel'>Submitted Turnkey Hours</label>
            <input 
              className='formInput'
              type="text"
              value={requestedTurnkeyHours}
              disabled={true}
            />
          </div>
          
          <div className='formCol'>
            <label className='formLabel'>Remaining Turnkey Hours</label>
            <input 
              className='formInput'
              type="text"
              value={remainingTurnkeyHours}
              disabled={true}
            />
          </div>
        </div>

        {/* Well Status Flags Section */}
        <SectionTitle title="Well Status Flags" />
        <div className='formRow'>
          <div className='formCol'>
            <label className='formLabel'>Wellhead Cutoff Complete</label>
            <div className='checkbox-container'>
              <input
                type="checkbox"
                id="wellheadCutoffComplete"
                checked={wellheadCutoffComplete}
                onChange={(e) => setWellheadCutoffComplete(e.target.checked)}
                disabled={!isEditing}
              />
            </div>
          </div>
          
          <div className='formCol'>
            <label className='formLabel'>Moving Back On</label>
            <div className='checkbox-container'>
              <input
                type="checkbox"
                id="movingBackOn"
                checked={movingBackOn}
                onChange={(e) => setMovingBackOn(e.target.checked)}
                disabled={!isEditing}
              />
            </div>
          </div>
          
          <div className='formCol'>
            <label className='formLabel'>Is Leaking</label>
            <div className='checkbox-container'>
              <input
                type="checkbox"
                id="isLeaking"
                checked={isLeaking}
                onChange={(e) => setIsLeaking(e.target.checked)}
                disabled={!isEditing}
              />
            </div>
          </div>
        </div>

        <div className='formRow'>
          <div className='formCol' style={{ width: '100%' }}>
            <label className='formLabel'>Notes</label>
            <textarea
              className='formInput'
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!isEditing}
              style={{ 
                minHeight: '100px',
                resize: 'vertical'
              }}
            />
          </div>
        </div>

        {/* Form Actions */}
        <Box sx={{ mt: 4, textAlign: 'right' }}>
          {canEdit && (
            isEditing ? (
              <>
                <button 
                  className="cancelButton hover" 
                  type="button" 
                  onClick={handleCancel}
                  style={{ 
                    marginRight: '10px', 
                    background: '#f5f5f5', 
                    color: '#333',
                    padding: '8px 16px',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                >
                  Cancel
                </button>
                <button className="saveButton hover" type="submit">
                  Submit Changes
                </button>
              </>
            ) : (
              <button className="editButton hover" type="button" onClick={handleEditClick}>
                Edit
              </button>
            )
          )}
        </Box>
      </form>
    </Paper>
  );
};

export default WellForm;
